/**
 * Numeric source-of-truth for the ShareCard export (share-an-insight design handoff).
 * Sizes are `cqw` units inside a `container-type: inline-size` box, so the same card
 * renders the small preview and, at each format's exact pixel width, the exported PNG.
 */

export type ShareCardLayout = 'stat' | 'split' | 'plaque'
export type ShareCardFormat = 'wide' | 'square' | 'portrait'
export type ShareCardSize = 'S' | 'M' | 'L'

export const FORMAT_DIMENSIONS: Record<ShareCardFormat, { width: number; height: number }> = {
  wide: { width: 1200, height: 630 },
  square: { width: 1080, height: 1080 },
  portrait: { width: 1080, height: 1350 },
}

export const FORMAT_ASPECT_RATIO: Record<ShareCardFormat, string> = {
  wide: '1200 / 630',
  square: '1 / 1',
  portrait: '1080 / 1350',
}

export const FORMAT_LABEL: Record<ShareCardFormat, string> = {
  wide: 'Wide',
  square: 'Square',
  portrait: 'Portrait',
}

export const FORMAT_DIMENSION_LABEL: Record<ShareCardFormat, string> = {
  wide: '1200 × 630',
  square: '1080 × 1080',
  portrait: '1080 × 1350',
}

export const SIZE_SCALE: Record<ShareCardSize, number> = {
  S: 0.82,
  M: 1,
  L: 1.2,
}

/** Font sizes in cqw, pre-scale. Multiply by SIZE_SCALE[size] before use. */
export const FONT_CQW = {
  statNumber: { stat: 19.2, split: 14, plaque: 12.5 } as Record<ShareCardLayout, number>,
  headline: { stat: 3.17, split: 3.5, plaque: 3.33 } as Record<ShareCardLayout, number>,
  labelEyebrow: { stat: 1.42, split: 1.42, plaque: 1.42 } as Record<ShareCardLayout, number>,
  sentence: { stat: 1.75, split: 1.83, plaque: 1.75 } as Record<ShareCardLayout, number>,
  // Source table gives a range for split/plaque ("1.58–1.66", "1.5–1.58"); the low end
  // is used for the fixed footer line, the high end for the brand line, on the same row.
  footer: { stat: 1.58, split: 1.58, plaque: 1.5 } as Record<ShareCardLayout, number>,
  brandLine: { stat: 1.58, split: 1.66, plaque: 1.58 } as Record<ShareCardLayout, number>,
}

export const BRAND_COLORS = {
  darkBlue: '#003673',
  lighterBlue: '#0454AE',
  darkerGrey: '#888888',
  lighterGrey: '#f0f0f0',
  coral: '#FA7861',
  peach: '#FFDABB',
  evergreen: '#0B6D5E',
  paleGreen: '#E1EADF',
} as const

/** The 8 headline/stat ink swatches offered in the modal, in display order. */
export const INK_SWATCHES: { hex: string; bordered: boolean }[] = [
  { hex: BRAND_COLORS.darkBlue, bordered: false },
  { hex: BRAND_COLORS.lighterBlue, bordered: false },
  { hex: BRAND_COLORS.evergreen, bordered: false },
  { hex: BRAND_COLORS.paleGreen, bordered: true },
  { hex: BRAND_COLORS.coral, bordered: false },
  { hex: BRAND_COLORS.peach, bordered: false },
  { hex: BRAND_COLORS.darkerGrey, bordered: false },
  { hex: '#FFFFFF', bordered: true },
]

export const DEFAULT_INK = BRAND_COLORS.peach

export const LAYOUT_GRADIENT: Record<ShareCardLayout, string> = {
  stat: `linear-gradient(90deg, ${BRAND_COLORS.darkBlue} 0 100%)`,
  split: `linear-gradient(90deg, ${BRAND_COLORS.evergreen} 0 45%, #ffffff 45% 100%)`,
  plaque: `linear-gradient(180deg, ${BRAND_COLORS.peach} 0 22%, #ffffff 22% 78%, ${BRAND_COLORS.peach} 78% 100%)`,
}

export const IMAGE_FORMATS: ShareCardFormat[] = ['wide', 'square', 'portrait']
